import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Download, Users, Loader2 } from "lucide-react";
import { useJobCandidaturas } from "@/hooks/useJobApplications";
import { useJobPostDetail } from "@/hooks/useJobPosts";
import { toast } from "@/hooks/use-toast";
import { MatchBadge } from "@/components/MatchBadge";
import { calculateMatchScore } from "@/lib/skills";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

import { APPLICATION_STATUS_CONFIG, getStatusLabel } from "@/lib/applicationStatus";

export default function EmpresaVagaCandidaturas() {
  const { id: vagaId } = useParams();
  const navigate = useNavigate();
  const { data: vaga } = useJobPostDetail(vagaId);
  const { candidaturas, isLoading, updateStatus } = useJobCandidaturas(vagaId);
  const [filter, setFilter] = useState("todos");

  // Fetch all candidate skills in one query
  const candidateIds = candidaturas.map(c => c.user_id);
  const { data: allCandidateSkills } = useQuery({
    queryKey: ["candidate_skills", candidateIds],
    enabled: candidateIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("student_skills" as any)
        .select("user_id, skill_id")
        .in("user_id", candidateIds);
      if (error) throw error;
      return data as any[];
    },
  });

  const requiredSkills = Array.isArray(vaga?.required_skills) ? (vaga.required_skills as string[]) : [];
  const niceSkills = Array.isArray(vaga?.nice_skills) ? (vaga.nice_skills as string[]) : [];
  const hasJobSkills = requiredSkills.length > 0 || niceSkills.length > 0;

  const getMatchForCandidate = (userId: string) => {
    const skills = (allCandidateSkills ?? []).filter((s: any) => s.user_id === userId).map((s: any) => s.skill_id);
    return calculateMatchScore(skills, requiredSkills, niceSkills);
  };

  const filtered = useMemo(() => {
    return candidaturas.filter(c => filter === "todos" || c.status === filter);
  }, [candidaturas, filter]);

  const stats = useMemo(() => ({
    total: candidaturas.length,
    active: candidaturas.filter(c => !["contratado", "nao_avancou", "desistiu"].includes(c.status)).length,
    hired: candidaturas.filter(c => c.status === "contratado").length,
  }), [candidaturas]);

  const handleStatusChange = async (applicationId: string, fromStatus: string, newStatus: string) => {
    try {
      await updateStatus.mutateAsync({ applicationId, fromStatus, toStatus: newStatus });
      toast({ title: "Status atualizado", description: `Candidato movido para ${APPLICATION_STATUS_CONFIG[newStatus]?.label}` });
    } catch {
      toast({ title: "Erro ao atualizar", variant: "destructive" });
    }
  };

  if (isLoading) {
    return <div className="page-container-desktop flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="page-container-desktop">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/empresa/vagas/${vagaId}`)}><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Candidaturas</h1>
            <p className="text-muted-foreground text-sm">{vaga?.title} • {filtered.length} candidatos</p>
          </div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-card rounded-xl p-4 border border-border text-center">
          <p className="text-2xl font-bold text-foreground">{stats.total}</p><p className="text-xs text-muted-foreground">Total</p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border text-center">
          <p className="text-2xl font-bold text-primary">{stats.active}</p><p className="text-xs text-muted-foreground">Em andamento</p>
        </div>
        <div className="bg-card rounded-xl p-4 border border-border text-center">
          <p className="text-2xl font-bold text-success">{stats.hired}</p><p className="text-xs text-muted-foreground">Contratados</p>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="flex items-center gap-3 mb-6 flex-wrap">
        {["todos", ...Object.keys(APPLICATION_STATUS_CONFIG)].map(s => {
          const count = s === "todos" ? candidaturas.length : candidaturas.filter(c => c.status === s).length;
          if (count === 0 && s !== "todos") return null;
          return (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === s ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"}`}>
              {s === "todos" ? "Todos" : APPLICATION_STATUS_CONFIG[s]?.label} ({count})
            </button>
          );
        })}
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="space-y-3">
        {filtered.map((c, i) => {
          const profile = c.profiles as any;
          const sc = APPLICATION_STATUS_CONFIG[c.status] ?? { label: c.status, color: "bg-gray-100 text-gray-700", nextStates: [] };
          const candidateMatch = hasJobSkills ? getMatchForCandidate(c.user_id) : null;
          return (
            <motion.div key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
              className="bg-card rounded-2xl p-5 border border-border hover:border-primary/20 transition-colors">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-lg font-semibold text-primary">{(profile?.full_name ?? "?").charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-foreground">{profile?.full_name ?? "Candidato"}</h3>
                      {candidateMatch && <MatchBadge percentage={candidateMatch.percentage} size="sm" showLabel={false} />}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {[profile?.city, profile?.state].filter(Boolean).join(", ")} {profile?.objective && `• ${profile.objective}`}
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-4">
                  {sc.nextStates.length > 0 && (
                    <Select onValueChange={(v) => handleStatusChange(c.id, c.status, v)}>
                      <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="Mover para..." /></SelectTrigger>
                      <SelectContent>
                        {sc.nextStates.map(ns => (
                          <SelectItem key={ns} value={ns}>{APPLICATION_STATUS_CONFIG[ns]?.label ?? ns}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/empresa/talento/${c.user_id}`)}>Perfil</Button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {filtered.length === 0 && (
        <div className="bg-muted/50 rounded-2xl p-12 text-center">
          <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-heading font-semibold text-foreground mb-2">Nenhuma candidatura encontrada</h3>
        </div>
      )}
    </div>
  );
}
